// src/shared/constants/seecLawCatalog.js
//
// Structured law catalog for SEEC-related exception reporting.
// This is intentionally separate from any detected exception rows.
// Exceptions should point at a law by `id`, then add filing-specific details.
//
// Note on sources:
// - Where possible, `sourceUrl` points to Connecticut's official sources:
//   - CGA statutory supplement pages (prdext2.cga.ct.gov)
//   - Connecticut eRegulations (eregulations.ct.gov)
// - For sections not present in the 2026 supplement pages, we reference
//   widely-used codifications (e.g., Justia/FindLaw) to provide stable access,
//   while still keeping citations in "Conn. Gen. Stat. § ..." form.
//
// Severity is an internal triage hint:
// - "high": likely enforcement / eligibility / significant penalties
// - "medium": material compliance obligations; may drive findings
// - "low": procedural/administrative; usually not a filing blocker

export const SEEC_LAW_CATALOG = {
  // -------------------------
  // SEEC enabling statutes
  // -------------------------

  "SEEC-0001": {
    id: "SEEC-0001",
    citation: "Conn. Gen. Stat. § 9-7a(a)-(c)",
    title:
      "SEEC established; commission composition/quorum; annual reporting obligations",
    summary:
      "Establishes the commission, sets bipartisan membership and quorum rules, and requires an annual report (including complaint dispositions, staffing, and expenditures) to the Governor and General Assembly.",
    category: "commission_governance",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0002": {
    id: "SEEC-0002",
    citation: "Conn. Gen. Stat. § 9-7a(b)(2)",
    title: "Public access to SEEC meetings via continuous Internet broadcast",
    summary:
      "Requires noticed commission meetings to be broadcast contemporaneously and continuously on an identified website, with specific public-access constraints (e.g., no required account).",
    category: "commission_governance",
    severity: "low",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0003": {
    id: "SEEC-0003",
    citation: "Conn. Gen. Stat. § 9-7a(d)(2)",
    title: "Executive director appointment cycle and legislative hearing on reappointment",
    summary:
      "Creates a quadrennial executive director appointment cycle, limits reappointment, and requires a joint public hearing prior to reappointment addressing SEEC operations and the health of the CEP.",
    category: "commission_governance",
    severity: "low",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0004": {
    id: "SEEC-0004",
    citation: "Conn. Gen. Stat. § 9-7a(e)",
    title:
      "Political activity restrictions for SEEC members and employees",
    summary:
      "Prohibits SEEC members/employees from candidacy, holding elected office, serving as political party officers, and holding certain party/committee roles.",
    category: "commission_governance",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0005": {
    id: "SEEC-0005",
    citation: "Conn. Gen. Stat. § 9-7a(g)(1)",
    title: "Complaint triage, timelines, and dismissal/referral mechanisms",
    summary:
      "Requires preliminary examination timelines for written complaints, provides pathways for dismissal, de minimis resolution, or investigation/docketing, and sets mechanisms tied to commission action deadlines (including longer-running processes and defined exceptions).",
    category: "procedure_rules",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0006": {
    id: "SEEC-0006",
    citation: "Conn. Gen. Stat. § 9-7a(g)(2)-(3)",
    title: "Secretary-of-the-State statements and expedited judicial proceeding",
    summary:
      "Creates deadlines and a court show-cause mechanism if SEEC does not act on statements filed by the Secretary of the State, and prioritizes such judicial proceedings.",
    category: "procedure_rules",
    severity: "low",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0010": {
    id: "SEEC-0010",
    citation: "Conn. Gen. Stat. § 9-7b(a)(1)",
    title: "Investigations, hearings, and subpoena power",
    summary:
      "Authorizes SEEC investigations on its own initiative or via complaint/statement, allows hearings, and provides subpoena authority under commission procedural rules.",
    category: "enforcement_powers",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0011": {
    id: "SEEC-0011",
    citation: "Conn. Gen. Stat. § 9-7b(a)(2)(D)",
    title: "Civil penalties for campaign finance/CEP violations",
    summary:
      "Authorizes civil penalties for violations of the campaign finance and CEP chapters, with penalty caps tied to a per-offense limit or a multiple of the improper contribution/payment, and requires an opportunity for a hearing before imposition.",
    category: "enforcement_powers",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0012": {
    id: "SEEC-0012",
    citation: "Conn. Gen. Stat. § 9-7b(a)(5)(A)",
    title: "Authority to inspect/audit committee accounts and records",
    summary:
      "Authorizes SEEC to inspect/audit treasurer records as required by the campaign finance and CEP chapters, with statutory constraints on timing of audit initiation near an election and on auditing caucuses.",
    category: "post_election_review",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0013": {
    id: "SEEC-0013",
    citation: "Conn. Gen. Stat. § 9-7b(a)(5)(B)",
    title: "Post-election audit lottery and statewide-office mandatory audits",
    summary:
      "Caps post-election random audits of candidate committees at 20% via a weighted public lottery for General Assembly districts, and requires auditing all candidate committees for statewide office.",
    category: "post_election_review",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0014": {
    id: "SEEC-0014",
    citation: "Conn. Gen. Stat. § 9-7b(a)(5)(C)",
    title: "Audit selection notice deadline",
    summary:
      "Requires written notice of selection for an audit by May 31 of the year following the election, and prohibits auditing a candidate committee if notice is not timely provided.",
    category: "post_election_review",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0015": {
    id: "SEEC-0015",
    citation: "Conn. Gen. Stat. § 9-7b(a)(5)(D)",
    title: "Audit completion deadline and annual reporting",
    summary:
      "Requires completion of lottery-selected audits within 12 months and annual reporting to the legislature on audits not completed within that period and reasons for delay.",
    category: "post_election_review",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  "SEEC-0016": {
    id: "SEEC-0016",
    citation: "Conn. Gen. Stat. § 9-7b(a)(6)-(8)",
    title: "Voluntary compliance, interagency consultation, and referrals",
    summary:
      "Authorizes informal compliance efforts, interagency consultation (e.g., Secretary of the State, Attorney General, Chief State’s Attorney), and referrals for further enforcement action.",
    category: "enforcement_powers",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_141.htm",
  },

  // -------------------------
  // SEEC procedural regulations
  // -------------------------

  "SEEC-0050": {
    id: "SEEC-0050",
    citation: "Regs., Conn. State Agencies §§ 9-7b-1—9-7b-97",
    title: "SEEC organizational rules of practice",
    summary:
      "Defines SEEC procedural rules for complaints/statements, investigations, subpoenas, hearings/contested cases, penalties, consent orders, and related process mechanics.",
    category: "procedure_rules",
    severity: "medium",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BD09AE155-0C00-C2A5-9733-35B4B3D9FD49%7D",
  },

  "SEEC-0051": {
    id: "SEEC-0051",
    citation: "Regs., Conn. State Agencies § 9-7b-26",
    title: "Procedure upon complaint/statement; notice windows",
    summary:
      "Specifies procedural steps following receipt of a complaint/statement, including notice and handling rules within SEEC process.",
    category: "procedure_rules",
    severity: "medium",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BD09AE155-0C00-C2A5-9733-35B4B3D9FD49%7D",
  },

  "SEEC-0052": {
    id: "SEEC-0052",
    citation: "Regs., Conn. State Agencies § 9-7b-28",
    title: "Investigatory subpoenas and enforcement in Superior Court",
    summary:
      "Sets requirements and process for issuing investigatory subpoenas and seeking court enforcement.",
    category: "procedure_rules",
    severity: "high",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BD09AE155-0C00-C2A5-9733-35B4B3D9FD49%7D",
  },

  "SEEC-0053": {
    id: "SEEC-0053",
    citation: "Regs., Conn. State Agencies § 9-7b-47",
    title: "No civil penalty without hearing unless waived",
    summary:
      "Requires an opportunity to be heard before SEEC may impose a civil penalty (unless waived via stipulation/consent).",
    category: "procedure_rules",
    severity: "high",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BD09AE155-0C00-C2A5-9733-35B4B3D9FD49%7D",
  },

  "SEEC-0054": {
    id: "SEEC-0054",
    citation: "Regs., Conn. State Agencies § 9-7b-48",
    title: "Factors for SEEC civil penalty determinations",
    summary:
      "Lists mitigating/aggravating factors used to determine civil penalty amounts (e.g., gravity, compliance, history, good faith).",
    category: "procedure_rules",
    severity: "medium",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BD09AE155-0C00-C2A5-9733-35B4B3D9FD49%7D",
  },

  "SEEC-0059": {
    id: "SEEC-0059",
    citation: "Proposed Regs., Conn. State Agencies §§ 9-7b-99—9-7b-102",
    title: "Post-election review procedures (proposed; PR2025-020)",
    summary:
      "Codifies procedures for post-election review selection and deadlines (including weighted lottery mechanics and compliance obligations), consistent with statutory changes referenced in the regulation-making record.",
    category: "post_election_review",
    severity: "medium",
    sourceUrl: "https://eregulations.ct.gov/eRegsPortal/Search/RMRView/PR2025-020",
    status: "proposed",
  },

  // -------------------------
  // Campaign finance (Title 9, Ch. 155)
  // -------------------------

  "SEEC-0100": {
    id: "SEEC-0100",
    citation: "Conn. Gen. Stat. § 9-601",
    title: "General definitions for campaign finance and CEP",
    summary:
      "Defines core campaign finance terms (e.g., committee types, candidate status, organization expenditure, solicitation terms) used throughout Chapters 155 and 157.",
    category: "registration",
    severity: "low",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_155.htm",
  },

  "SEEC-0101": {
    id: "SEEC-0101",
    citation: "Conn. Gen. Stat. § 9-601a",
    title: "Contribution definition and exclusions",
    summary:
      "Defines what constitutes a contribution (and what does not) for campaign finance and CEP purposes.",
    category: "receipts",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_155.htm",
  },

  "SEEC-0102": {
    id: "SEEC-0102",
    citation: "Conn. Gen. Stat. § 9-601b",
    title: "Expenditure definition and exclusions",
    summary:
      "Defines expenditures for campaign finance and CEP and identifies excluded activity types.",
    category: "expenditures",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_155.htm",
  },

  "SEEC-0103": {
    id: "SEEC-0103",
    citation: "Conn. Gen. Stat. § 9-601d",
    title: "Independent expenditures and reporting triggers",
    summary:
      "Authorizes independent expenditures subject to statutory restrictions and sets reporting/filing requirements and exceptions for entities/persons making independent expenditures.",
    category: "independent_expenditures",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_155.htm",
  },

  "SEEC-0104": {
    id: "SEEC-0104",
    citation: "Conn. Gen. Stat. § 9-604",
    title: "Formation of candidate committee; exceptions; exploratory committees",
    summary:
      "Requires each candidate for a particular office (or town committee member) to form a single candidate committee, subject to statutory exceptions and provisions governing exploratory committees.",
    category: "registration",
    severity: "high",
    sourceUrl: "https://codes.findlaw.com/ct/title-9-elections/ct-gen-st-sect-9-604/",
  },

  "SEEC-0105": {
    id: "SEEC-0105",
    citation: "Conn. Gen. Stat. § 9-606(a)",
    title: "Treasurer responsibilities for deposits, reporting, filings, and internal records",
    summary:
      "Makes the treasurer responsible for receiving/depositing/reporting funds, making/reporting expenditures and unpaid obligations, filing required statements, and keeping internal records for reported entries.",
    category: "disclosure_reporting",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-155/section-9-606-formerly-sec-9-333h/",
  },

  "SEEC-0106": {
    id: "SEEC-0106",
    citation: "Conn. Gen. Stat. § 9-606(b)",
    title: "Anonymous contributions handling",
    summary:
      "Prohibits depositing anonymous contributions and requires forwarding such funds as directed by statute for disposition.",
    category: "receipts",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-155/section-9-606-formerly-sec-9-333h/",
  },

  "SEEC-0107": {
    id: "SEEC-0107",
    citation: "Conn. Gen. Stat. § 9-607",
    title: "Making of expenditures and related requirements",
    summary:
      "Governs how expenditures may be made, including reporting and responsibility rules tied to campaign-related spending and documentation expectations.",
    category: "expenditures",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-155/section-9-607-formerly-sec-9-333i/",
  },

  "SEEC-0108": {
    id: "SEEC-0108",
    citation: "Conn. Gen. Stat. § 9-608",
    title: "Campaign finance statements; surplus/deficit treatment",
    summary:
      "Requires treasurers to file periodic campaign finance statements and governs how surpluses/deficits are treated, including committee termination requirements where applicable.",
    category: "disclosure_reporting",
    severity: "high",
    sourceUrl: "https://codes.findlaw.com/ct/title-9-elections/ct-gen-st-sect-9-608/",
  },

  "SEEC-0109": {
    id: "SEEC-0109",
    citation: "Conn. Gen. Stat. § 9-610(g)-(i)",
    title: "Communicator lobbyist restrictions on contributions, solicitations, and bundling",
    summary:
      "Restricts communicator lobbyist (and related persons/entities) contributions above specified limits, prohibits certain solicitations, and prohibits bundling for specified committee types and offices.",
    category: "receipts",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-155/section-9-610-formerly-sec-9-333l/",
  },

  "SEEC-0110": {
    id: "SEEC-0110",
    citation: "Conn. Gen. Stat. § 9-621",
    title: "Political advertising disclaimers and attribution",
    summary:
      "Establishes statutory requirements for disclaimers/attribution in political advertising, including media-specific rules and updated definitions tied to modern communications.",
    category: "advertising_disclaimer",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_155.htm",
  },

  "SEEC-0111": {
    id: "SEEC-0111",
    citation: "Conn. Gen. Stat. § 9-623",
    title: "Penalties for campaign finance violations; late filing fees",
    summary:
      "Creates civil/criminal penalty framework for campaign finance violations, including statutory late filing fees for required filings and enforcement consequences.",
    category: "penalties",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-155/section-9-623-formerly-sec-9-333y/",
  },

  // -------------------------
  // eCRIS / electronic filing (Title 9, Ch. 156)
  // -------------------------

  "SEEC-0150": {
    id: "SEEC-0150",
    citation: "Conn. Gen. Stat. § 9-675",
    title: "Electronic submission of campaign finance disclosure statements (eCRIS)",
    summary:
      "Requires SEEC to provide a web-based program and reporting specs and mandates electronic filing for specified committees/thresholds; includes waiver concepts and scope rules.",
    category: "disclosure_reporting",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-156/section-9-675-formerly-sec-9-348ee/",
  },

  "SEEC-0151": {
    id: "SEEC-0151",
    citation: "Conn. Gen. Stat. § 9-676",
    title: "Conversion of paper filings to electronic data",
    summary:
      "Directs SEEC to convert paper-format campaign finance statement data into an electronic format, within available appropriations.",
    category: "disclosure_reporting",
    severity: "medium",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-156/section-9-676-formerly-sec-9-348ff/",
  },

  "SEEC-0152": {
    id: "SEEC-0152",
    citation: "Conn. Gen. Stat. § 9-677",
    title: "Public access to computerized campaign finance statement data",
    summary:
      "Provides for public access to computerized campaign finance statement data, consistent with statutory design for disclosure and transparency.",
    category: "disclosure_reporting",
    severity: "low",
    sourceUrl: "https://law.justia.com/codes/connecticut/title-9/chapter-156/",
  },

  // -------------------------
  // Citizens' Election Program (Title 9, Ch. 157)
  // -------------------------

  "SEEC-0200": {
    id: "SEEC-0200",
    citation: "Conn. Gen. Stat. § 9-701",
    title: "Citizens' Election Fund established",
    summary:
      "Establishes the Citizens' Election Fund as a nonlapsing account administered by the State Treasurer and specifies permissible uses under the CEP.",
    category: "cep",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_157.htm",
  },

  "SEEC-0201": {
    id: "SEEC-0201",
    citation: "Conn. Gen. Stat. § 9-704(d)-(e)",
    title: "Qualifying contribution certifications and nonqualifying contribution handling",
    summary:
      "Requires contributor certifications above specified thresholds and defines categories of nonqualifying contributions that must be returned or transmitted (as applicable) for CEP compliance.",
    category: "cep",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_157.htm",
  },

  "SEEC-0202": {
    id: "SEEC-0202",
    citation: "Conn. Gen. Stat. § 9-706",
    title: "Grant application process and required certifications/accounting",
    summary:
      "Governs CEP grant applications (including timing and required certifications), requires a cumulative itemized accounting conforming to campaign finance statement requirements, and establishes SEEC review/approval mechanics.",
    category: "cep",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_157.htm",
  },

  "SEEC-0203": {
    id: "SEEC-0203",
    citation: "Conn. Gen. Stat. § 9-712",
    title: "Supplemental campaign finance statements and excess expenditures declarations",
    summary:
      "Requires weekly supplemental statements in specified primary/general contexts and imposes rapid filing obligations for declarations of excess expenditures once thresholds are exceeded.",
    category: "cep",
    severity: "high",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-157/section-9-712/",
  },

  "SEEC-0204": {
    id: "SEEC-0204",
    citation: "Conn. Gen. Stat. § 9-720",
    title: "Required documentation list for audits; safe-harbor outside the list",
    summary:
      "Requires SEEC to publish a complete and detailed list of documentation/internal records required for audit compliance, to include it in large guidance documents, and limits treasurer liability for records not on that list.",
    category: "post_election_review",
    severity: "high",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_157.htm",
  },

  "SEEC-0205": {
    id: "SEEC-0205",
    citation: "Regs., Conn. State Agencies § 9-607-1",
    title: "Recordkeeping requirements for campaign staff and professional services",
    summary:
      "Requires specific internal records (e.g., written agreements and contemporaneous invoices/records) to substantiate payments for campaign staff and professional personnel services.",
    category: "cep",
    severity: "high",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/RCSA/Title_9Subtitle_9-607Section_9-607-1/",
  },

  "SEEC-0206": {
    id: "SEEC-0206",
    citation: "Regs., Conn. State Agencies § 9-702-1",
    title: "CEP expenditure limits and attribution of expenditures",
    summary:
      "Provides regulatory attribution rules for expenditures in the context of the CEP expenditure limits framework.",
    category: "cep",
    severity: "medium",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/RCSA/Title_9Subtitle_9-702Section_9-702-1/",
  },

  "SEEC-0207": {
    id: "SEEC-0207",
    citation: "Regs., Conn. State Agencies §§ 9-706-1—9-706-3",
    title: "Participating candidate use of campaign funds; documentation; payment not final determination",
    summary:
      "Defines regulatory requirements for how participating candidates may use campaign funds, emphasizes contemporaneous documentation expectations, and clarifies that payment approval does not constitute a final determination.",
    category: "cep",
    severity: "high",
    sourceUrl:
      "https://eregulations.ct.gov/eRegsPortal/Browse/getDocument?guid=%7BF09AE155-0900-C534-AEAF-522CE1A1760B%7D",
  },

  "SEEC-0208": {
    id: "SEEC-0208",
    citation: "Conn. Gen. Stat. § 9-750",
    title: "Backstop funding mechanism for Citizens' Election Fund insufficiency",
    summary:
      "Provides a mechanism for depositing tax revenues into the Citizens' Election Fund in the event of an insufficiency, to allow grant payments.",
    category: "cep",
    severity: "medium",
    sourceUrl: "https://prdext2.cga.ct.gov/2026/sup/chap_157.htm",
  },

  // -------------------------
  // Municipal public financing (Title 9, Ch. 158)
  // -------------------------

  "SEEC-0300": {
    id: "SEEC-0300",
    citation: "Conn. Gen. Stat. § 9-760",
    title: "Pilot program for public financing for certain municipal offices",
    summary:
      "Directs SEEC to establish a pilot program for public financing of campaigns for certain municipal offices and to approve municipality implementation plans and participation criteria.",
    category: "cep",
    severity: "medium",
    sourceUrl:
      "https://law.justia.com/codes/connecticut/title-9/chapter-158/section-9-760/",
  },

  // -------------------------
  // Public Acts / change tracking
  // -------------------------

  "SEEC-9000": {
    id: "SEEC-9000",
    citation: "2025 Conn. Pub. Act 25-26",
    title: "Campaign finance reforms affecting SEEC audits and compliance definitions",
    summary:
      "Implements statutory changes including adjustments affecting SEEC post-election/audit lottery framework and related campaign finance definitions and advertising rules; useful as a change-control anchor for rule updates.",
    category: "change_tracking",
    severity: "low",
    sourceUrl:
      "https://www.cga.ct.gov/2025/act/Pa/pdf/2025PA-00026-R00SB-01405-PA.PDF",
  },
};

export const SEEC_LAW_LIST = Object.values(SEEC_LAW_CATALOG);

export const SEEC_FILING_EXCEPTION_LAW_IDS = [
  "SEEC-0101",
  "SEEC-0102",
  "SEEC-0103",
  "SEEC-0104",
  "SEEC-0105",
  "SEEC-0106",
  "SEEC-0107",
  "SEEC-0108",
  "SEEC-0109",
  "SEEC-0110",
  "SEEC-0111",
  "SEEC-0150",
  "SEEC-0151",
  "SEEC-0201",
  "SEEC-0202",
  "SEEC-0203",
  "SEEC-0204",
  "SEEC-0205",
  "SEEC-0206",
  "SEEC-0207",
];

export const SEEC_FILING_EXCEPTION_LAWS = SEEC_FILING_EXCEPTION_LAW_IDS.map(
  (lawId) => SEEC_LAW_CATALOG[lawId],
).filter(Boolean);

export function getSeecLawById(lawId) {
  return SEEC_LAW_CATALOG[lawId] || null;
}

export function getSeecFilingExceptionLawById(lawId) {
  return SEEC_FILING_EXCEPTION_LAW_IDS.includes(lawId)
    ? SEEC_LAW_CATALOG[lawId] || null
    : null;
}

/*
This catalog is anchored in the commission's enabling statute (including explicit
audit/post-election review constraints and penalty authority), the official
procedural regulations (RCSA 9-7b-1 through 9-7b-97), core campaign finance and
independent expenditure definitions and advertising rules, the e-filing mandate,
and key Citizens' Election Program compliance mechanisms (including recordkeeping
rules and the statutory "documentation list" requirement).

Known gaps and how to keep the catalog current
Because Connecticut publishes some statutory updates via supplements and public
acts, the most reliable way to keep your catalog current is to treat "law
onboarding" as a tiny release process:

- Track statutory change events: Public acts and supplements are the "what
  changed" layer. For example, Public Act 25-26 is directly tied to
  audit/post-election review changes and related campaign finance updates.
- Track regulatory change events: Connecticut eRegulations hosts both current
  regulations and regulation-making records (with tracking numbers) that show
  what's proposed and why (e.g., PR2025-020 for post-election review procedures).
- Pin operationally critical deadlines/thresholds: The most "exception-worthy"
  rules are those tied to filing obligations and audits/post-election review
  selection and time bounds.
- Keep the catalog citation-granular: When a single statute section includes
  multiple machine-checkable obligations, prefer separate law IDs by subsection
  so exceptions can cite the exact authority cleanly.

SEEC Law Catalog for Automated Exception Reporting
What this catalog covers and why this structure works
The fastest path to dependable AI-driven exception reporting is to separate:

1. a law catalog (stable, curated citations + plain-English rule summaries), from
2. detected exceptions (filing-specific facts: committee name, time period,
   amount, transaction IDs, etc.).

That separation matches how the Connecticut enforcement framework is organized:
the commission's core authorities sit in its enabling statutes, while the
underlying compliance rules come from the campaign finance and public financing
chapters and their implementing regulations.

Two statutory features are particularly AI-engine friendly for automated checks:

- The commission's audit/post-election review authority is explicit and
  time-bounded.
- Electronic filing is required for many filers, and the commission is directed
  to provide the web-based/standards infrastructure (eCRIS and vendor specs).

The code scaffold (IDs, citation, summary, category, severity, sourceUrl) is the
foundation for mapping each detected exception to a single authoritative legal
anchor.
*/
